import { Box, usePropsResolution } from 'native-base';
import React from 'react';

import { Platform } from 'react-native';

import { Heading } from '../Heading';
import { Image } from '../Image';
import { Link } from '../Link';
import { Text } from '../Text';
import type { HTMLProps, Renderers } from './HTML.types';
import { getDocument, renderDocument } from './HTML.utils';

export const getRenderers = (props: any): Renderers => ({
  h1: (children, index) => (
    <Heading size="heading-1" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  h2: (children, index) => (
    <Heading size="heading-2" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  h3: (children, index) => (
    <Heading size="heading-3" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  h4: (children, index) => (
    <Heading size="heading-4" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  h5: (children, index) => (
    <Heading size="heading-5" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  h6: (children, index) => (
    <Heading size="heading-6" key={index} marginBottom={4}>
      {children}
    </Heading>
  ),
  p: (children, index) => (
    <Text {...props._text} color="default" key={index} marginBottom={4}>
      {children}
    </Text>
  ),
  b: (children, index) => (
    <Text {...props._text} bold key={index}>
      {children}
    </Text>
  ),
  strong: (children, index) => (
    <Text {...props._text} bold key={index}>
      {children}
    </Text>
  ),
  i: (children, index) => (
    <Text {...props._text} key={index} italic>
      {children}
    </Text>
  ),
  em: (children, index) => (
    <Text {...props._text} key={index} italic>
      {children}
    </Text>
  ),
  div: (children, index) => <Box key={index}>{children}</Box>,
  ol: (children, index) => (
    <Box key={index} {...props._list}>
      {children}
    </Box>
  ),
  ul: (children, index) => (
    <Box key={index} {...props._list}>
      {children}
    </Box>
  ),
  li: (children, index) => (
    <Box key={index} {...props._item}>
      <Box {...props._bullet} />
      <Text {...props._text} color="default" key={index}>
        {children}
      </Text>
    </Box>
  ),
  a: (children, index, attributes) => (
    <Link
      href={attributes?.href}
      key={index}
      fontSize="md"
      lineHeight="md"
      fontFamily="sans-serif"
      // @ts-ignore
      _android={{ transform: [{ translateY: 5.5 }] }}
      _ios={{ mt: '-3px' }}
    >
      {children}
    </Link>
  ),
  img: (_, _index, attributes) => {
    return (
      <Image
        autoResize
        resizeMode="contain"
        source={{ uri: attributes?.src }}
        width="100%"
      />
    );
  },
  video: (children, _index, attributes) => {
    if (Platform.OS === 'web') {
      return (
        <video controls {...attributes}>
          {children}
        </video>
      );
    }
    return <Box>{children}</Box>;
  },
  source: (_children, index, attributes) => {
    if (Platform.OS === 'web') {
      return <source {...attributes} />;
    }
    return (
      <Text color="inherit" bold key={index}>
        Video not supported on this platform
      </Text>
    );
  },
});

const HTML: React.FunctionComponent<HTMLProps> = ({ renderers, source }) => {
  const resolvedProps = usePropsResolution('HTML', {});
  const document = getDocument(source);

  const documentRenderers = React.useMemo(() => {
    if (!renderers) {
      return getRenderers(resolvedProps);
    }

    return Object.assign(getRenderers(resolvedProps), renderers);
  }, [renderers, resolvedProps]);

  return (
    <React.Fragment>
      {renderDocument(document, documentRenderers)}
    </React.Fragment>
  );
};

export default HTML;
